import React, { useEffect, useRef, useState, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Collapse } from "react-bootstrap";
import classNames from "classnames";
import FeatherIcon from "feather-icons-react";
import CaseCategorySelectModal from "../Components/CaseCategorySelectModal";
import {
  applyCaseCategoryToPath,
  getDefaultCaseSectionUrl,
  isCaseSectionMenuItem,
  pathsMatchCaseRoute,
  shouldShowCaseCategoryPicker,
} from "../util/caseCategoryNavigation";
import { findAllParent, findMenuItem } from "../helpers/Menu";
import { changeHTMLAttribute } from "../util/layout";
import { getMasterClaimTypeList } from "../redux/actions/MasterActions";

const menuClick = () => {
  document.body.style.overflow = "visible";
  changeHTMLAttribute("class", "");
  var backdrop = document.getElementById("custom-backdrop");
  if (backdrop) {
    document.body.removeChild(backdrop);
    document.body.style.overflow = "visible";
  }
};

const MenuItemWithChildren = ({
  item,
  linkClassName,
  subMenuClassNames,
  activeMenuItems,
  toggleMenu,
  menuItems,
  onCaseCategoryRequest,
}) => {
  const [open, setOpen] = useState(activeMenuItems.includes(item.key));

  useEffect(() => {
    setOpen(activeMenuItems.includes(item.key));
  }, [activeMenuItems, item]);

  const toggleMenuItem = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (
      shouldShowCaseCategoryPicker() &&
      (item.key === "active_case" || item.key === "my_case")
    ) {
      onCaseCategoryRequest({
        targetUrl: getDefaultCaseSectionUrl(item.key),
        sectionLabel: item.label,
      });
      return;
    }

    const status = !open;
    setOpen(status);
    if (toggleMenu) toggleMenu(item, status);
  };

  return (
    <li className={classNames("menu-item", { "menuitem-active": open })}>
      <Link
        to="#"
        onClick={toggleMenuItem}
        data-menu-key={item.key}
        aria-expanded={open}
        className={classNames("menu-link", linkClassName, {
          "menuitem-active": activeMenuItems.includes(item.key) ? "active" : "",
        })}
      >
        {item.icon && (
          <span className="menu-icon">
            <FeatherIcon icon={item.icon} />{" "}
          </span>
        )}
        <span className="menu-text"> {item.label} </span>
        {!item.badge ? (
          <span className="menu-arrow"></span>
        ) : (
          <span className={`badge bg-${item.badge.variant} rounded-pill ms-auto`}>
            {item.badge.text}
          </span>
        )}
      </Link>
      <Collapse in={open}>
        <div>
          <ul className={classNames(subMenuClassNames)}>
            {(item.children || []).map((child, i) => (
              <React.Fragment key={i}>
                {child.children ? (
                  <MenuItemWithChildren
                    item={child}
                    linkClassName={activeMenuItems.includes(child.key) ? "active" : ""}
                    activeMenuItems={activeMenuItems}
                    subMenuClassNames="sub-menu"
                    toggleMenu={toggleMenu}
                    menuItems={menuItems}
                    onCaseCategoryRequest={onCaseCategoryRequest}
                  />
                ) : (
                  <MenuItem
                    item={child}
                    className={activeMenuItems.includes(child.key) ? "menuitem-active" : ""}
                    linkClassName={activeMenuItems.includes(child.key) ? "active" : ""}
                    menuItems={menuItems}
                    onCaseCategoryRequest={onCaseCategoryRequest}
                  />
                )}
              </React.Fragment>
            ))}
          </ul>
        </div>
      </Collapse>
    </li>
  );
};

const MenuItem = ({ item, className, linkClassName, menuItems, onCaseCategoryRequest }) => (
  <li className={classNames("menu-item", className)}>
    <MenuItemLink
      item={item}
      className={linkClassName}
      menuItems={menuItems}
      onCaseCategoryRequest={onCaseCategoryRequest}
    />
  </li>
);

const MenuItemLink = ({ item, className, menuItems, onCaseCategoryRequest }) => {
  const handleClick = (e) => {
    menuClick();

    if (
      shouldShowCaseCategoryPicker() &&
      item.url &&
      isCaseSectionMenuItem(menuItems, item)
    ) {
      e.preventDefault();
      onCaseCategoryRequest({
        targetUrl: item.url,
        sectionLabel: item.label,
      });
    }
  };

  return (
    <Link
      to={item.url}
      onClick={handleClick}
      target={item.target}
      className={classNames("side-nav-link-ref menu-link", className)}
      data-menu-key={item.key}
    >
      {item.icon && (
        <span className="menu-icon">
          <FeatherIcon icon={item.icon} />{" "}
        </span>
      )}
      <span className="menu-text"> {item.label} </span>
      {item.badge && (
        <span className={`badge bg-${item.badge.variant} `}>
          {item.badge.text}
        </span>
      )}
    </Link>
  );
};

const AppMenu = ({ menuItems }) => {
  let location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    AuthReducer: { auth },
  } = useSelector((state) => state);
  const menuRef = useRef(null);
  const [activeMenuItems, setActiveMenuItems] = useState([]);
  const [categoryPicker, setCategoryPicker] = useState({
    show: false,
    targetUrl: "",
    title: "Select Case Type",
  });

  const toggleMenu = (menuItem, show) => {
    if (show) {
      setActiveMenuItems([menuItem.key, ...findAllParent(menuItems, menuItem)]);
      return;
    }
    setActiveMenuItems((prev) => prev.filter((key) => key !== menuItem.key));
  };

  const handleCaseCategoryRequest = ({ targetUrl, sectionLabel }) => {
    setCategoryPicker({
      show: true,
      targetUrl,
      title: sectionLabel ? `Select ${sectionLabel} Type` : "Select Case Type",
    });
  };

  const handleCategorySelect = (category) => {
    const destination = applyCaseCategoryToPath(categoryPicker.targetUrl, category);
    setCategoryPicker({ show: false, targetUrl: "", title: "Select Case Type" });
    if (auth?.token) {
      dispatch(getMasterClaimTypeList(auth.token, category));
    }
    navigate(destination);
  };

  const activeMenu = useCallback(() => {
    const div = document.getElementById("main-side-menu");
    let matchingMenuItem = null;

    if (div) {
      let items = div.getElementsByClassName("side-nav-link-ref");
      for (let i = 0; i < items.length; ++i) {
        let trimmedURL = location.pathname.replace(process.env.PUBLIC_URL, "");
        const itemPath = items[i]?.pathname?.replace(process.env.PUBLIC_URL, "");
        if (pathsMatchCaseRoute(trimmedURL, itemPath)) {
          matchingMenuItem = items[i];
          break;
        }
      }

      if (matchingMenuItem) {
        const mid = matchingMenuItem.getAttribute("data-menu-key");
        const activeMt = findMenuItem(menuItems, mid);
        if (activeMt) {
          setActiveMenuItems([activeMt.key, ...findAllParent(menuItems, activeMt)]);
        }
      }
    }
  }, [location, menuItems]);

  useEffect(() => {
    activeMenu();
  }, [activeMenu]);

  return (
    <>
      <ul className="menu" ref={menuRef} id="main-side-menu">
        {(menuItems || []).map((item, idx) => (
          <React.Fragment key={idx}>
            {item.isTitle ? (
              <li className={classNames("menu-title", { "mt-2": idx !== 0 })}>
                {item.label}
              </li>
            ) : (
              <>
                {item.children ? (
                  <MenuItemWithChildren
                    item={item}
                    toggleMenu={toggleMenu}
                    subMenuClassNames="sub-menu"
                    activeMenuItems={activeMenuItems}
                    linkClassName="menu-link"
                    menuItems={menuItems}
                    onCaseCategoryRequest={handleCaseCategoryRequest}
                  />
                ) : (
                  <MenuItem
                    item={item}
                    linkClassName="menu-link"
                    className={activeMenuItems.includes(item.key) ? "menuitem-active" : ""}
                    menuItems={menuItems}
                    onCaseCategoryRequest={handleCaseCategoryRequest}
                  />
                )}
              </>
            )}
          </React.Fragment>
        ))}
      </ul>

      <CaseCategorySelectModal
        show={categoryPicker.show}
        onHide={() =>
          setCategoryPicker({ show: false, targetUrl: "", title: "Select Case Type" })
        }
        onSelect={handleCategorySelect}
        title={categoryPicker.title}
      />
    </>
  );
};

export default AppMenu;
